/* eslint-disable camelcase */
import { FF_UNIFIED_CK_ENDPOINTS } from 'components/admin/utils/feature-flags';
import { useAddResponseFeedback } from 'components/conversations/hooks/useResponses';
import {
  FeedbackSource,
  FeedbackType as UnifiedFeedbackType,
  MessageType,
} from 'components/feedback/hooks/types';
import { useSubmitFeedback as useUnifiedSubmitFeedback } from 'components/feedback/hooks/useSubmitFeedback';
import { FeedbackSummary } from 'components/shopper/components/automations/sampleMessages/types';
import { FeatureFlagsContext } from 'controllers/contexts/featureFlags';
import { useUser } from 'controllers/contexts/user';
import { useContext, useEffect, useState } from 'react';

import { Message, Subscriber } from '../types';
import { getCrossShopId } from '../utils/viewedShop';
import {
  getShopperAutomationMessageGuid,
  isShopperMessage,
} from './messageGuards';

export type FeedbackDisplay = 'POSITIVE' | 'NEGATIVE' | '';

/** Resolves the current thumbs state from the message's stored feedback. */
export const deriveFeedbackDisplay = (message: Message): FeedbackDisplay => {
  const feedbackType = message.feedbackSummary?.feedbackType;
  if (feedbackType === 'thumbs_down') return 'NEGATIVE';
  if (feedbackType === 'thumbs_up') return 'POSITIVE';
  const raw = message.feedback?.toUpperCase();
  if (raw === 'POSITIVE') return 'POSITIVE';
  if (raw === 'NEGATIVE') return 'NEGATIVE';
  return '';
};

export interface UseMessageFeedbackResult {
  canSubmitFeedback: boolean;
  feedbackDisplay: FeedbackDisplay;
  isLocked: boolean;
  showFeedbackPanel: boolean;
  panelFeedback: FeedbackSummary | null;
  // The cross-shop id used for submissions, exposed so callers with their own
  // submit paths (e.g. IT feedback) target the same shop.
  shopId: number | undefined;
  handleThumbsUp: () => void;
  handleThumbsDown: () => void;
  handleFeedbackPanelSubmit: (category: string, feedbackText: string) => void;
  handleFeedbackPanelClose: () => void;
}

/**
 * Encapsulates the standard (non-Infinity-Test) message feedback behavior:
 * derives the current thumbs state, manages optimistic + panel state, and
 * submits via the unified or legacy endpoint depending on the
 * `FF_UNIFIED_CK_ENDPOINTS` flag. Shared by the conversation thread
 * (MessageMetadata) and the Message Details drawer so both surfaces submit
 * identical payloads against the same sent message.
 */
export const useMessageFeedback = (
  message: Message,
  subscriber: Subscriber | null,
): UseMessageFeedbackResult => {
  const { mutate: addResponseFeedback } = useAddResponseFeedback();

  const { hasFlag } = useContext(FeatureFlagsContext) as unknown as {
    hasFlag?: (flag: string) => boolean;
  };
  const useUnified = hasFlag?.(FF_UNIFIED_CK_ENDPOINTS) ?? false;
  const { mutate: submitUnifiedFeedback } = useUnifiedSubmitFeedback();

  // Feedback endpoints scope to the authed user's shop by default. When an
  // admin/sales agent views another shop's conversation, the submit must
  // target the viewed shop explicitly or the message lookup 404s.
  const { user } = useUser();
  const shopId = getCrossShopId(subscriber?.shop_id, user?.shop_id);

  const apiFeedback = deriveFeedbackDisplay(message);
  const [optimisticFeedback, setOptimisticFeedback] =
    useState<FeedbackDisplay | null>(null);

  useEffect(() => {
    setOptimisticFeedback(null);
  }, [apiFeedback]);

  const isLocked = apiFeedback !== '' || optimisticFeedback !== null;

  const [showFeedbackPanel, setShowFeedbackPanel] = useState(false);

  const feedbackDisplay: FeedbackDisplay =
    optimisticFeedback ??
    (showFeedbackPanel && apiFeedback !== 'NEGATIVE'
      ? 'NEGATIVE'
      : apiFeedback);

  const responseType = isShopperMessage(message) ? 'shopper' : 'sender';
  const messageGuid = getShopperAutomationMessageGuid(message);
  const canSubmitFeedback = responseType === 'shopper' || messageGuid != null;
  const panelFeedback =
    message.feedbackSummary?.feedbackType === 'thumbs_down'
      ? message.feedbackSummary
      : null;

  const handleThumbsUp = () => {
    if (isLocked || !canSubmitFeedback) return;
    if (useUnified) {
      submitUnifiedFeedback({
        messageType: MessageType.SHOPPER_RESPONSE,
        feedbackSource: FeedbackSource.CONVERSATIONS_VIEW,
        feedbackType: UnifiedFeedbackType.THUMBS_UP,
        messageId: message.sent_message_id,
        messageGuid,
        messageText: message.text,
        subscriberId: subscriber?.subscriber.id,
        shopId,
      });
    } else {
      addResponseFeedback({
        messageId: message.sent_message_id,
        feedback_type: 'positive',
        message: message.text,
        subscriberId: subscriber?.subscriber.id ?? 0,
        responseType,
        messageGuid,
        message_text: message.text,
        shopId,
      });
    }
    setOptimisticFeedback('POSITIVE');
    setShowFeedbackPanel(false);
  };

  const handleThumbsDown = () => {
    if (isLocked || !canSubmitFeedback) return;
    setShowFeedbackPanel(!showFeedbackPanel);
  };

  const handleFeedbackPanelSubmit = (
    category: string,
    feedbackText: string,
  ) => {
    if (!canSubmitFeedback) return;
    if (useUnified) {
      submitUnifiedFeedback({
        messageType: MessageType.SHOPPER_RESPONSE,
        feedbackSource: FeedbackSource.CONVERSATIONS_VIEW,
        feedbackType: UnifiedFeedbackType.THUMBS_DOWN,
        messageId: message.sent_message_id,
        messageGuid,
        messageText: message.text,
        subscriberId: subscriber?.subscriber.id,
        feedbackValue: feedbackText || undefined,
        feedbackMetadata: category ? { selectedPills: [category] } : undefined,
        shopId,
      });
    } else {
      addResponseFeedback({
        messageId: message.sent_message_id,
        feedback_type: 'negative',
        message: feedbackText,
        subscriberId: subscriber?.subscriber.id ?? 0,
        responseType,
        messageGuid,
        shopId,
        ...(responseType === 'shopper'
          ? {
              message_text: message.text,
              data: { selectedPills: [category] },
            }
          : {
              feedbackValue: feedbackText,
              feedbackMetadata: { selectedPills: [category] },
              message_text: message.text,
            }),
      });
    }
    setOptimisticFeedback('NEGATIVE');
  };

  const handleFeedbackPanelClose = () => {
    setShowFeedbackPanel(false);
  };

  return {
    canSubmitFeedback,
    feedbackDisplay,
    isLocked,
    showFeedbackPanel,
    panelFeedback,
    shopId,
    handleThumbsUp,
    handleThumbsDown,
    handleFeedbackPanelSubmit,
    handleFeedbackPanelClose,
  };
};
